import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Camera,
  FileText,
  CheckCircle,
  AlertCircle,
  Save,
  Upload,
  Wallet,
  Building,
  Calendar,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

export default function VendedorPerfil() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [perfil, setPerfil] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showContrato, setShowContrato] = useState(false);
  const [aceptaContrato, setAceptaContrato] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    direccion: "",
    documento_id: "",
    foto_url: "",
  });
  const [datosBancarios, setDatosBancarios] = useState({
    frecuencia_pago: "semanal",
    banco: "",
    tipo_cuenta: "",
    numero_cuenta: "",
    titular_cuenta: "",
  });
  const [savingBancarios, setSavingBancarios] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [perfilRes, contratoRes] = await Promise.all([
        api.get("/usuarios/perfil"),
        api.get("/contrato"),
      ]);
      setPerfil(perfilRes.data);
      setContrato(contratoRes.data);
      setForm({
        nombre: perfilRes.data.nombre || "",
        telefono: perfilRes.data.telefono || "",
        direccion: perfilRes.data.direccion || "",
        documento_id: perfilRes.data.documento_id || "",
        foto_url: perfilRes.data.foto_url || "",
      });
      setDatosBancarios({
        frecuencia_pago: perfilRes.data.frecuencia_pago || "semanal",
        banco: perfilRes.data.banco || "",
        tipo_cuenta: perfilRes.data.tipo_cuenta || "",
        numero_cuenta: perfilRes.data.numero_cuenta || "",
        titular_cuenta: perfilRes.data.titular_cuenta || "",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Use JPG, PNG, WEBP o GIF");
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 2MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/usuarios/perfil/foto", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setForm({ ...form, foto_url: response.data.foto_url });
      toast.success("Foto actualizada correctamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/usuarios/perfil", form);
      toast.success("Perfil actualizado");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleAceptarContrato = async () => {
    if (!aceptaContrato) {
      toast.error("Debe marcar que acepta los términos");
      return;
    }
    try {
      await api.put("/usuarios/perfil/contrato", { aceptado: true });
      toast.success("Contrato aceptado. ¡Ya puedes comenzar a vender!");
      fetchData();
      setShowContrato(false);
    } catch (error) {
      toast.error("Error al aceptar contrato");
    }
  };

  const handleSaveBancarios = async (e) => {
    e.preventDefault();
    setSavingBancarios(true);
    try {
      await api.put("/usuarios/perfil", datosBancarios);
      toast.success("Datos bancarios actualizados");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar datos bancarios");
    } finally {
      setSavingBancarios(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Completa todos los campos");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.put("/usuarios/perfil/password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-perfil">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Mi Perfil</h1>
        <p className="text-torta-muted">Gestiona tu información personal</p>
      </div>

      {/* Estado del Contrato */}
      <div
        className={`rounded-2xl p-5 ${
          perfil?.contrato_aceptado
            ? "bg-green-50 border border-green-200"
            : "bg-amber-50 border border-amber-200"
        }`}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {perfil?.contrato_aceptado ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-amber-600" />
            )}
            <div>
              <p
                className={`font-medium ${
                  perfil?.contrato_aceptado ? "text-green-800" : "text-amber-800"
                }`}
              >
                {perfil?.contrato_aceptado
                  ? "Contrato Firmado"
                  : "Contrato Pendiente"}
              </p>
              <p
                className={`text-sm ${
                  perfil?.contrato_aceptado ? "text-green-600" : "text-amber-600"
                }`}
              >
                {perfil?.contrato_aceptado
                  ? `Aceptado el ${new Date(perfil.fecha_contrato).toLocaleDateString("es-CO")}`
                  : "Debes aceptar el contrato para poder vender"}
              </p>
            </div>
          </div>
          {!perfil?.contrato_aceptado && (
            <Button
              onClick={() => setShowContrato(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Ver y Firmar Contrato
            </Button>
          )}
        </div>
      </div>

      {/* Foto de Perfil */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="font-display text-xl text-torta-dark mb-4">Foto de Perfil</h2>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            {form.foto_url ? (
              <img
                src={form.foto_url}
                alt="Perfil"
                className="w-28 h-28 rounded-full object-cover border-4 border-torta-brown"
              />
            ) : (
              <div className="w-28 h-28 bg-torta-cream rounded-full flex items-center justify-center border-4 border-torta-brown">
                <User className="w-12 h-12 text-torta-brown" />
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 bg-torta-brown text-white p-2 rounded-full hover:bg-torta-brown/90 transition-colors"
            >
              {uploadingPhoto ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Camera className="w-5 h-5" />
              )}
            </button>
          </div>
          
          <div className="flex-1 text-center sm:text-left">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="mb-3"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingPhoto ? "Subiendo..." : "Subir Foto"}
            </Button>
            <p className="text-sm text-torta-muted">
              Formatos: JPG, PNG, WEBP, GIF. Máx 2MB
            </p>
          </div>
        </div>
      </div>

      {/* Formulario de Perfil */}
      <form onSubmit={handleSave} className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="font-display text-xl text-torta-dark">Información Personal</h2>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4 text-torta-brown" />
              Nombre completo
            </Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Tu nombre"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-torta-brown" />
              Correo electrónico
            </Label>
            <Input value={perfil?.email} disabled className="bg-gray-50" />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-torta-brown" />
              Teléfono
            </Label>
            <Input
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="300 123 4567"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-torta-brown" />
              Documento de identidad
            </Label>
            <Input
              value={form.documento_id}
              onChange={(e) => setForm({ ...form, documento_id: e.target.value })}
              placeholder="CC 1234567890"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-torta-brown" />
              Dirección
            </Label>
            <Input
              value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              placeholder="Calle, número, barrio, ciudad"
            />
          </div>
        </div>

        <div className="bg-torta-cream rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-medium text-torta-dark">Zona asignada</p>
              <p className="text-torta-muted text-sm">{perfil?.zona || "Sin zona asignada"}</p>
            </div>
            <div>
              <p className="font-medium text-torta-dark">Meta diaria</p>
              <p className="text-torta-brown font-display text-lg">
                ${perfil?.meta_diaria?.toLocaleString("es-CO")}
              </p>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Guardando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Guardar Cambios
            </span>
          )}
        </Button>
      </form>

      {/* Sección de Método de Pago */}
      <form onSubmit={handleSaveBancarios} className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-display text-xl text-torta-dark">Mi Método de Pago</h2>
            <p className="text-torta-muted text-sm">Configura cómo quieres recibir tus comisiones</p>
          </div>
        </div>

        {/* Nota de Frecuencia de Pago */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800">Frecuencia de Pago</p>
              <p className="text-blue-700 text-sm mt-1">
                <strong>Comisiones base:</strong> Pago semanal (cada lunes)<br />
                <strong>Bonos extra:</strong> Pago mensual (primer día del mes)
              </p>
            </div>
          </div>
        </div>

        {/* Datos Bancarios */}
        <div className="border-t border-torta-sand pt-5 space-y-4">
          <Label className="flex items-center gap-2 text-torta-dark font-medium">
            <Building className="w-4 h-4 text-torta-brown" />
            Datos Bancarios
          </Label>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Banco</Label>
              <select
                value={datosBancarios.banco}
                onChange={(e) => {
                  const newBanco = e.target.value;
                  // Si es Llave Breb-B, auto-seleccionar tipo "llave"
                  if (newBanco === "llave_brebb") {
                    setDatosBancarios({ 
                      ...datosBancarios, 
                      banco: newBanco, 
                      tipo_cuenta: "llave",
                      numero_cuenta: "" // Reset numero para que ingresen nombre de llave
                    });
                  } else {
                    setDatosBancarios({ 
                      ...datosBancarios, 
                      banco: newBanco,
                      tipo_cuenta: datosBancarios.tipo_cuenta === "llave" ? "" : datosBancarios.tipo_cuenta
                    });
                  }
                }}
                className="w-full h-12 px-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-torta-brown/50"
              >
                <option value="">Seleccionar banco</option>
                <option value="bancolombia">Bancolombia</option>
                <option value="davivienda">Davivienda</option>
                <option value="bogota">Banco de Bogotá</option>
                <option value="occidente">Banco de Occidente</option>
                <option value="bbva">BBVA</option>
                <option value="popular">Banco Popular</option>
                <option value="avvillas">AV Villas</option>
                <option value="colpatria">Scotiabank Colpatria</option>
                <option value="nequi">Nequi</option>
                <option value="daviplata">Daviplata</option>
                <option value="llave_brebb">Llave Breb-B</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Tipo de Cuenta</Label>
              {datosBancarios.banco === "llave_brebb" ? (
                <div className="w-full h-12 px-3 rounded-lg border border-gray-200 bg-gray-100 flex items-center text-gray-700">
                  Llave
                </div>
              ) : (
                <select
                  value={datosBancarios.tipo_cuenta}
                  onChange={(e) => setDatosBancarios({ ...datosBancarios, tipo_cuenta: e.target.value })}
                  className="w-full h-12 px-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-torta-brown/50"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="ahorros">Cuenta de Ahorros</option>
                  <option value="corriente">Cuenta Corriente</option>
                </select>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">
              {datosBancarios.banco === "llave_brebb" ? "Nombre de Llave" : "Número de Cuenta"}
            </Label>
            <Input
              value={datosBancarios.numero_cuenta}
              onChange={(e) => setDatosBancarios({ ...datosBancarios, numero_cuenta: e.target.value })}
              placeholder={datosBancarios.banco === "llave_brebb" ? "Ej: celular, correo o documento" : "Ej: 123456789012"}
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Titular de la Cuenta</Label>
            <Input
              value={datosBancarios.titular_cuenta}
              onChange={(e) => setDatosBancarios({ ...datosBancarios, titular_cuenta: e.target.value })}
              placeholder="Nombre como aparece en el banco"
              className="h-12"
            />
          </div>
        </div>

        {/* Advertencia */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-amber-800 text-sm">
            <strong>Importante:</strong> Asegúrate de que los datos bancarios sean correctos. 
            Las transferencias se realizarán a la cuenta registrada según la frecuencia seleccionada.
          </p>
        </div>

        <Button
          type="submit"
          disabled={savingBancarios}
          className="w-full h-12 bg-green-600 hover:bg-green-700"
        >
          {savingBancarios ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Guardando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Guardar Datos de Pago
            </span>
          )}
        </Button>
      </form>

      {/* Sección de Cambio de Contraseña */}
      <form onSubmit={handleChangePassword} className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-display text-xl text-torta-dark">Cambiar Contraseña</h2>
            <p className="text-torta-muted text-sm">Actualiza tu contraseña de acceso</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Contraseña Actual</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Ingresa tu contraseña actual"
                className="h-12 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Nueva Contraseña</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="h-12 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Confirmar Nueva Contraseña</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Repite la nueva contraseña"
              className="h-12"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={savingPassword}
          className="w-full h-12 bg-red-600 hover:bg-red-700"
        >
          {savingPassword ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Actualizando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Cambiar Contraseña
            </span>
          )}
        </Button>
      </form>

      {/* Modal Contrato */}
      {showContrato && contrato && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header - fixed */}
            <div className="p-4 sm:p-6 border-b border-torta-sand flex-shrink-0">
              <h2 className="font-display text-xl sm:text-2xl text-torta-dark">{contrato.titulo}</h2>
              <p className="text-torta-muted text-sm">Empresa: {contrato.empresa}</p>
            </div>
            
            {/* Content - scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
              <pre className="whitespace-pre-wrap font-body text-sm text-torta-dark leading-relaxed">
                {contrato.contenido}
              </pre>
            </div>
            
            {/* Footer - fixed at bottom */}
            <div className="p-4 sm:p-6 border-t border-torta-sand bg-torta-cream/50 flex-shrink-0">
              <div className="flex items-start gap-3 mb-4 sm:mb-6">
                <Checkbox
                  id="acepto"
                  checked={aceptaContrato}
                  onCheckedChange={setAceptaContrato}
                  className="mt-1 flex-shrink-0"
                />
                <Label htmlFor="acepto" className="text-xs sm:text-sm text-torta-dark cursor-pointer">
                  He leído y acepto los términos y condiciones del contrato de prestación de
                  servicios independiente. Entiendo que este NO es un contrato laboral y que
                  actuaré como comerciante independiente.
                </Label>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowContrato(false)}
                  className="w-full sm:flex-1 order-2 sm:order-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAceptarContrato}
                  disabled={!aceptaContrato}
                  className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 order-1 sm:order-2"
                  data-testid="btn-firmar-contrato"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Aceptar y Firmar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
